import type { Method } from './types';

import { authenticate } from '../auth';
import { API, buildRequestHeaders } from './api';

/**
 * The client for server_pray's module surfaces (`/api/studio/v1` and the other `/api/<surface>/v1` prefixes), whose
 * responses follow the Module API Standard instead of the legacy envelope:
 *
 * - a resource answers `{ data }`, and a list `{ data: [...], pagination: { next_cursor, has_more, total? } }`;
 * - an error answers its real HTTP status with an RFC 9457 problem+json body, thrown here as `ApiProblemError`;
 * - a 401 means the session lapsed, so the request re-authenticates once and is retried. A 403 is an answer, not a
 *   lapsed session, so it is thrown at once, without the legacy client's re-authenticate-and-retry.
 *
 * Decoding is tolerant, as clients of the standard must be: a field the client does not know is ignored, and each
 * resource's decoder maps a value it does not know to a safe default rather than failing the whole response.
 */

export type ApiProblemFieldError = {
  /** The request field the error is about, in the wire's terms (`name`, `root.children[2]`). */
  field: string;
  code: string;
  message: string;
};

type ApiProblem = {
  status: number;
  code: string;
  title: string;
  detail: string | null;
  requestId: string | null;
  errors: ApiProblemFieldError[];
};

export class ApiProblemError extends Error {
  readonly status: number;

  /** The problem's machine code, as `conflict` or `validation_failed`: what a caller branches on, with the status. */
  readonly code: string;

  readonly title: string;

  readonly detail: string | null;

  /** Quote this when reporting a failure: it finds the request in server_pray's logs. */
  readonly requestId: string | null;

  /** One per field a validation failure is about. Empty for any other problem. */
  readonly errors: ApiProblemFieldError[];

  constructor(problem: ApiProblem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiProblemError';
    this.status = problem.status;
    this.code = problem.code;
    this.title = problem.title;
    this.detail = problem.detail;
    this.requestId = problem.requestId;
    this.errors = problem.errors;
  }
}

export type ApiPagination = {
  /** Hand it back as `cursor` for the next page. `null` on the last page. */
  nextCursor: string | null;
  hasMore: boolean;
  /** Only when the route counts; most lists do not. */
  total: number | null;
};

export type ApiCollection<T> = {
  items: T[];
  pagination: ApiPagination;
};

// Generic helpers are function declarations: Studio's tests read `.ts` as TSX, where `<T>(…) =>` parses as a tag.
function stringOr<T>(value: unknown, fallback: T): string | T {
  return typeof value === 'string' ? value : fallback;
}

const fieldErrorsOf = (value: unknown): ApiProblemFieldError[] => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
    .map((entry) => ({
      field: stringOr(entry.field, ''),
      code: stringOr(entry.code, 'invalid'),
      message: stringOr(entry.message, ''),
    }));
};

/**
 * A failed response as a problem, read tolerantly: the HTTP status is the truth, and a body that is not
 * problem+json (a proxy's HTML page, an empty body) still becomes a problem with that status.
 */
export const toApiProblem = (status: number, body: unknown): ApiProblemError => {
  const problem = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};

  return new ApiProblemError({
    status,
    code: stringOr(problem.code, 'unknown'),
    title: stringOr(problem.title, `Request failed with status ${status}`),
    detail: stringOr(problem.detail, null),
    requestId: stringOr(problem.request_id, null),
    errors: fieldErrorsOf(problem.errors),
  });
};

type HttpFailure = { response?: { status: number; data?: unknown } };

const responseOf = (error: unknown): HttpFailure['response'] => (error as HttpFailure)?.response;

/** An HTTP answer becomes a problem. A failure with no response (offline, timed out) propagates as it is. */
const asProblem = (error: unknown): unknown => {
  const response = responseOf(error);

  return response ? toApiProblem(response.status, response.data) : error;
};

type SendOptions = {
  params?: Record<string, unknown>;
  data?: Record<string, unknown>;
};

async function send<T>(method: Method, path: string, { params, data }: SendOptions = {}): Promise<T> {
  const request = async () => {
    const response = await API.request<T>({
      method,
      url: path,
      params,
      data,
      responseType: 'json',
      headers: buildRequestHeaders(),
    });

    return response.data;
  };

  let lapsed: unknown;
  try {
    return await request();
  } catch (error) {
    if (responseOf(error)?.status !== 401) throw asProblem(error);
    lapsed = error;
  }

  // Studio's re-authentication can fail with something that is not an HTTP answer (it reads the user it gets back),
  // so a session that cannot be renewed is answered with the 401 that asked for it.
  try {
    await authenticate();
  } catch {
    throw asProblem(lapsed);
  }

  try {
    return await request();
  } catch (error) {
    throw asProblem(error);
  }
}

/** The resource in a `{ data }` body, through the resource's decoder. */
export function resourceOf<T>(body: unknown, decode: (wire: unknown) => T): T {
  return decode((body as { data?: unknown } | null)?.data);
}

/** The items of a `{ data: [...], pagination }` body, each through the decoder, and the pagination. */
export function collectionOf<T>(body: unknown, decode: (wire: unknown) => T): ApiCollection<T> {
  const { data, pagination } = (body ?? {}) as { data?: unknown; pagination?: Record<string, unknown> };

  return {
    items: Array.isArray(data) ? data.map((item) => decode(item)) : [],
    pagination: {
      nextCursor: stringOr(pagination?.next_cursor, null),
      hasMore: pagination?.has_more === true,
      total: typeof pagination?.total === 'number' ? pagination.total : null,
    },
  };
}

/** Answers each response's body; `resourceOf` and `collectionOf` read it. */
export const moduleApi = {
  get(path: string, params?: Record<string, unknown>): Promise<unknown> {
    return send('get', path, { params });
  },
  post(path: string, data?: Record<string, unknown>): Promise<unknown> {
    return send('post', path, { data });
  },
  patch(path: string, data: Record<string, unknown>): Promise<unknown> {
    return send('patch', path, { data });
  },
  delete(path: string): Promise<unknown> {
    return send('delete', path);
  },
};
